
import { Component, Inject, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { filter } from 'rxjs/operators';

import { FolderService } from '../../../Core/services/folder.service';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { TagsService } from '../../../../Common/Tags/services/tags.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { FilterService } from '../../../../Common/Filter/services/abstract-filter.service';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { SearchStatesEnum } from '../../enums/search-states.enum';
import { MentionSearchQueryModel } from '../../models/mention-search-query.model';
import { DuplicatesStatusEnum } from '../../../../Common/Filter/enums/duplicates-status.enum';
import { TagGroupsService } from '../../../../Common/Tags/services/tag-groups.service';
import { MediaSubscriptionService } from '../../../Core/services/media-subscription.service';
import { SourceGroupService } from '../../../../Common/Sources/services/source-group.service';
import { SourceService } from '../../../../Common/Sources/services/source.service';
import { ThemeService } from '../../../../Social/Intelligence/services/theme.service';
import { AgentService } from '../../../../Social/Intelligence/services/agent.service';
import { MentionSearchService } from '../../../Core/services/mention-search.service';
import { TopicsService } from '../../../Core/services/topics.service';
import { MonitoringSourcePickerFieldComponent } from './monitoring-source-picker-field.component';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { CachedDateService } from '../../../../Common/MonitorShared/services/cached-date.service';

/**
 * This component is designed to work with pagebuilder as well as being used on its own.
 *
 * It will allow the user to select a datasource from the Monitoring Feature module
 * and emit an event with the type of datasource selected along with the id of the relevant datasource.
 *
 * In the event this component is used with pageBuilders "blockform" component this component will
 * emit the event which will be added to display element of the relevant block.
 */
@Component({
    selector: 'agpr-monitoring-source-picker-with-facet-field',
    templateUrl: './monitoring-source-picker-field.component.html',
    styleUrls: ['./monitoring-source-picker-field.component.scss'],
    providers: [
        {
            provide: FilterService,
            useExisting: MentionSearchService
        }
    ],
    standalone: false
})
export class MonitoringSourcePickerFieldWithFacetComponent extends MonitoringSourcePickerFieldComponent implements OnInit, OnDestroy, OnChanges {

    readonly messageSeverityEnum = messageSeverityEnum;

    constructor(
        public topicsService: TopicsService,
        public monitorConfigService: MonitorConfigService,
        public folderService: FolderService,
        public tagsService: TagsService,
        public tagGroupsService: TagGroupsService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public sourceGroupService: SourceGroupService,
        public sourceService: SourceService,
        public themeService: ThemeService,
        public agentService: AgentService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public mentionSearchService: MentionSearchService,
        public localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService,
        public cachedDateService: CachedDateService
    ) {
        super(
            topicsService,
            monitorConfigService,
            folderService,
            tagsService,
            tagGroupsService,
            mediaSubscriptionService,
            sourceGroupService,
            sourceService,
            themeService,
            agentService,
            localizationService,
            currentUserStateService,
            appBaseHref,
            cachedDateService
        );
    }

    ngOnInit() {
        // don't allow grouping
        this.mentionSearchService.duplicates.showGroup = false;
        // make the default date query "all"
        this.mentionSearchService.setDateQuery(DateQueryModel.getAllQuery());

        // initialize the default search criteria
        this.updateSearchCriteria();

        super.ngOnInit();

        this.setupSubscriptions();
        this.setupDateQuerySubscription();
        this.initializeDataSourceOptions();
    }

    setupSubscriptions(): void {
        super.setupSubscriptions();

        if (this.field.orderedAccountFilters) {
            this.mentionSearchService.setOrderedAccountFilters(this.field.orderedAccountFilters);
        }
        if (this.field.orderedMercuryFilters) {
            this.mentionSearchService.setOrderedMercuryFilters(this.field.orderedMercuryFilters);
        }

        this.subscriptions.push(
            this.mentionSearchService.getLatestSearchCriteria().pipe(filter(value => !!value)).subscribe(
                searchCriteria => {
                    this.value.combinedSearchCriteria = searchCriteria;
                    const clone = this.value.filterSearchCriteria?.clone();
                    this.value.filterSearchCriteria = this.mentionSearchService.generateFilterSearchCriteria();
                    this.value.filterSearchCriteria.query.ave = this.mentionSearchService.ave.getValue();
                    this.value.filterSearchCriteria.sortField = clone.sortField;
                    this.value.filterSearchCriteria.sortOrder = clone.sortOrder;

                    this.changes.emit(this.value);
                }
            ),
            this.mentionSearchService.getMentionCount().subscribe(
                count => {
                    this.setNumberOfResults(count);
                }
            ),
            this.mentionSearchService.getSearchState().subscribe(
                state => {
                    this.isLoadingResults = state.type === SearchStatesEnum.loading;
                }
            )
        );
    }

    public async updateSearchCriteria(sourceType?: any): Promise<void> {
        const sourceTypeToUse = sourceType ?? this.sourceType;
        await super.updateSearchCriteria(sourceTypeToUse);
        this.mentionSearchService.setSearchCriteria(this.value.sourceSearchCriteria, sourceTypeToUse);
    }

    setSearchDateQuery(dateQuery: DateQueryModel): void {
        this.mentionSearchService.setDateQuery(dateQuery);
    }

    clearSearch(): void {
        this.mentionSearchService.clearSearch();
    }

    search(): void {
        if (this.sourceIsValid()) {
            this.mentionSearchService.search(this.field.showFilters);
        }
    }

    setAccountFilters(): void {
        this.mentionSearchService.setAccountFilters(this.sourceType);
    }

    populateFiltersFromSearchQuery(filterQuery: MentionSearchQueryModel): void {
        this.mentionSearchService.populateFiltersFromSearchQuery(filterQuery);
    }

    setDuplicateFilter(duplicatesStatus: DuplicatesStatusEnum): void {
        this.mentionSearchService.setDuplicateFilter(duplicatesStatus);
    }

    setSavedFiltersFromIds(ids: number[]): void {
        this.mentionSearchService.setSavedFiltersFromIds(ids);
    }

    /**
     * Resets the filters if the filters dropdown is being used.
     */
    resetFilters(): void {
        if (this.field.showFilters) {
            this.mentionSearchService.clearFilters();
        }
    }

    setupDateQuerySubscription() {
        this.subscriptions.push(
            this.mentionSearchService.getDateQuery().subscribe(
                dateQuery => {
                    this.dateQuery = dateQuery;
                }
            )
        );
    }
}
